import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { CustomCursor } from "@/components/site/CustomCursor";
import { EASE } from "@/components/site/Reveal";
import Home from "@/pages/Home";
import About from "@/pages/About";
import ServicesHair from "@/pages/ServicesHair";
import ServicesLaser from "@/pages/ServicesLaser";
import Gallery from "@/pages/Gallery";
import Testimonials from "@/pages/Testimonials";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import { SITE_ROUTES, NOT_FOUND_META } from "@/config/routes";
import { SITE_URL } from "@/config/site";

// Applies the per-page title, description and canonical URL from the shared
// routes config during client-side navigation (the static per-route HTML
// shells already carry them for direct loads / crawlers).
const RouteMeta = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // Normalise trailing slashes — Apache/LiteSpeed redirect /about to /about/
    // before serving that folder's index.html, so the router sees "/about/".
    const normalized = pathname !== "/" ? pathname.replace(/\/+$/, "") : "/";
    const meta = SITE_ROUTES.find((r) => r.path === normalized) || NOT_FOUND_META;
    document.title = meta.title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", meta.description);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE_URL}${normalized === "/" ? "/" : normalized}`);
  }, [pathname]);
  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Elfsight platform.js (loaded once in index.html) scans the DOM only when it
// executes. After client-side navigation a widget container (Home / Reviews)
// can mount later, so re-inject the script whenever one hasn't initialised.
const useElfsightRefresh = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const t = setTimeout(() => {
      const containers = document.querySelectorAll("[data-elfsight-app-lazy]");
      const needsInit = Array.from(containers).some((el) => !el.querySelector("iframe"));
      if (needsInit) {
        const s = document.createElement("script");
        s.src = "https://elfsightcdn.com/platform.js";
        s.async = true;
        document.body.appendChild(s);
      }
    }, 1500);
    return () => clearTimeout(t);
  }, [pathname]);
};

const Shell = () => {
  const location = useLocation();
  useElfsightRefresh();
  return (
    <div className="grain">
      <Navbar />
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: EASE }}
        >
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services-hair" element={<ServicesHair />} />
            <Route path="/services-laser" element={<ServicesLaser />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </motion.main>
      </AnimatePresence>
      <Footer />
    </div>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <RouteMeta />
      <CustomCursor />
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#1C1B19",
            color: "#FAF6F0",
            border: "1px solid #A9875F",
            fontFamily: "Jost, sans-serif",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontSize: "12px",
            borderRadius: "2px",
          },
        }}
      />
      <Shell />
    </BrowserRouter>
  );
}

export default App;
